/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {forElement} from '@/shell/util/forElement';
import {
  AcademicLicensesSubroute,
  EvaluationsSubroute,
  LicensesSubroute,
  MainRoute,
  OpenSourceSubroute
} from '@/shell/router';

/**
 * Returns the route (path relative to host root) of the evaluation with the given UUID.
 */
export function getEvaluationRoute(evaluationUuid: string): string {
  return `/${MainRoute.EVALUATIONS}/${EvaluationsSubroute.ACTIVE}/${evaluationUuid}`;
}

/**
 * Returns the route (path relative to host root) of the open source license request with
 * the given UUID.
 */
export function getOpenSourceRequestRoute(requestUuid: string): string {
  return `/${MainRoute.LICENSES}/${LicensesSubroute.OPEN_SOURCE}/${OpenSourceSubroute.REQUESTS}/${requestUuid}`;
}

/**
 * Returns the route (path relative to host root) of the open source license request with
 * the given UUID.
 */
export function getAcademicRequestRoute(requestUuid: string): string {
  return `/${MainRoute.LICENSES}/${LicensesSubroute.ACADEMIC}/${AcademicLicensesSubroute.REQUESTS}/${requestUuid}`;
}

/**
 * Copies the evaluation URL to the clipboard.
 *
 * @param evaluationUuid UUID of the evaluation whose URL should be copied.
 */
export async function copyEvaluationUrlToClipboard(evaluationUuid: string): Promise<void> {
  const route = getEvaluationRoute(evaluationUuid);
  await copyUrlToClipboard(route);
}

/**
 * Copies the commercial license's URL to the clipboard.
 *
 * @param licenseUuid UUID of the license whose URL should be copied.
 */
export async function copyCommercialLicenseUrlToClipboard(licenseUuid: string): Promise<void> {
  const route = `/${MainRoute.LICENSES}/${LicensesSubroute.COMMERCIAL}/${licenseUuid}`;
  await copyUrlToClipboard(route);
}

/**
 * Copies the open source license request's URL to the clipboard.
 *
 * @param requestUuid UUID of the license request whose URL should be copied.
 */
export async function copyOpenSourceRequestUrlToClipboard(requestUuid: string): Promise<void> {
  const route = getOpenSourceRequestRoute(requestUuid);
  await copyUrlToClipboard(route);
}

/**
 * Copies the open source license's URL to the clipboard.
 *
 * @param licenseUuid UUID of the license whose URL should be copied.
 */
export async function copyOpenSourceLicenseUrlToClipboard(licenseUuid: string): Promise<void> {
  const route = `/${MainRoute.LICENSES}/${LicensesSubroute.OPEN_SOURCE}/${OpenSourceSubroute.LICENSES}/${licenseUuid}`;
  await copyUrlToClipboard(route);
}

/**
 * Copies the academic license request's URL to the clipboard.
 *
 * @param requestUuid UUID of the license request whose URL should be copied.
 */
export async function copyAcademicRequestUrlToClipboard(requestUuid: string): Promise<void> {
  const route = getAcademicRequestRoute(requestUuid);
  await copyUrlToClipboard(route);
}

/**
 * Copies the academic license's URL to the clipboard.
 *
 * @param licenseUuid UUID of the license whose URL should be copied.
 */
export async function copyAcademicLicenseUrlToClipboard(licenseUuid: string): Promise<void> {
  const route = `/${MainRoute.LICENSES}/${LicensesSubroute.ACADEMIC}/${AcademicLicensesSubroute.LICENSES}/${licenseUuid}`;
  await copyUrlToClipboard(route);
}

/**
 * Copies the demo license's URL to the clipboard.
 *
 * @param licenseUuid UUID of the license whose URL should be copied.
 */
export async function copyDemoLicenseUrlToClipboard(licenseUuid: string): Promise<void> {
  const route = `/${MainRoute.LICENSES}/${LicensesSubroute.DEMO}/${AcademicLicensesSubroute.LICENSES}/${licenseUuid}`;
  await copyUrlToClipboard(route);
}

/**
 * Returns the URL fo rthe specified route. A route here is a path relative to the host's root.
 * It may or may not start with a leading forward slash -- in both cases it's interpreted as a path
 * relative to the host's root.
 *
 * @param route a path relative to the host's root to construct full URL from
 * @return a full URL, which consists of the host's URL and the given route
 */
function fullUrlByRoute(route: string): string {
  const hostUrl = getHostUrl();
  const normalizedRoute = !route.startsWith('/')
      ? '/' + route
      : route;
  return `${hostUrl}${normalizedRoute}`;
}

/**
 * Copies the URL defined by the provided route to the clipboard. The provided route (which is
 * relative to the host's root) is converted to the full URL by prepending it with the host URL
 * before copying. The host string may or may not have the leading forward slash character. In both
 * cases it is interpreted as a path relative to the host's root.
 *
 * @param route the route (a path relative to the host's root) whose URL should be copied
 *              to clipboard
 */
async function copyUrlToClipboard(route: string) {
  if (!document.activeElement) {
    // the clipboard API works only when some element is focused, so make sure that
    // something is focused
    document.documentElement.focus();
  }
  await forElement(() => document.activeElement);
  const url = fullUrlByRoute(route);
  navigator.clipboard.writeText(url);
}

function getHostUrl(): string {
  return `${window.location.protocol}//${window.location.host}`;
}
